import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import {
  SmtpSettings, StorageSettings, FeedbackPlatformSettings,
  FeedbackTicket, FeedbackTicketDetail, FeedbackMessage
} from '../models/feedback.model';
import {
  AnomalySettings, BillingSettings, RateLimitsSettings,
  OpsSettings, DeploymentSettings, RestartStatus, AnnouncementsSettings,
  StripeSettings, BillingoSettings,
  ExchangeRatesResponse, ExchangeRateHistory, SecurityScoreSettings,
  VatRatesResponse, UserLimitsSettings, BetaSettings,
} from '../models/platform-settings.model';

const ADMIN_BASE = `${environment.apiBaseUrl}/v1/admin/platform-admin/platform/settings`;
const FEEDBACK_BASE = `${environment.apiBaseUrl}/v1/admin/platform-admin/feedback`;
const EXCHANGE_RATES_BASE = `${environment.apiBaseUrl}/v1/admin/platform-admin/exchange-rates`;
const VAT_RATES_BASE = `${environment.apiBaseUrl}/v1/admin/platform-admin/vat-rates`;

@Injectable({ providedIn: 'root' })
export class PlatformSettingsService {
  private readonly http = inject(HttpClient);

  // SMTP
  getSmtp() {
    return this.http.get<SmtpSettings>(`${ADMIN_BASE}/smtp`);
  }
  saveSmtp(cfg: SmtpSettings) {
    return this.http.put<void>(`${ADMIN_BASE}/smtp`, cfg);
  }
  testSmtp() {
    return this.http.post<{ status: string }>(`${ADMIN_BASE}/smtp/test`, {});
  }

  // Storage
  getStorage() {
    return this.http.get<StorageSettings>(`${ADMIN_BASE}/storage`);
  }
  saveStorage(cfg: StorageSettings) {
    return this.http.put<void>(`${ADMIN_BASE}/storage`, cfg);
  }
  testStorage() {
    return this.http.post<{ status: string }>(`${ADMIN_BASE}/storage/test`, {});
  }

  // Feedback settings
  getFeedbackSettings() {
    return this.http.get<FeedbackPlatformSettings>(`${ADMIN_BASE}/feedback`);
  }
  saveFeedbackSettings(cfg: FeedbackPlatformSettings) {
    return this.http.put<void>(`${ADMIN_BASE}/feedback`, cfg);
  }

  // Security score (tunable weights / bands / stale-days)
  getSecurityScore() {
    return this.http.get<SecurityScoreSettings>(`${ADMIN_BASE}/security-score`);
  }
  saveSecurityScore(cfg: SecurityScoreSettings) {
    return this.http.put<SecurityScoreSettings>(`${ADMIN_BASE}/security-score`, cfg);
  }

  readonly adminUnreadCount = signal(0);
  readonly pendingRestart = signal(false);

  loadAdminUnreadCount() {
    this.http.get<{ count: number }>(`${FEEDBACK_BASE}/unread-count`)
      .subscribe({ next: r => this.adminUnreadCount.set(r.count) });
  }

  markAdminRead(id: string) {
    return this.http.post<void>(`${FEEDBACK_BASE}/${id}/read`, {});
  }

  // Platform admin feedback list + management
  listAllTickets(params: { status?: string; type?: string; tenant_id?: string } = {}) {
    return this.http.get<FeedbackTicket[]>(FEEDBACK_BASE, { params });
  }
  getTicketAdmin(id: string) {
    return this.http.get<FeedbackTicketDetail>(`${FEEDBACK_BASE}/${id}`);
  }
  setStatus(id: string, status: string) {
    return this.http.patch<void>(`${FEEDBACK_BASE}/${id}/status`, { status });
  }
  addAdminMessage(id: string, body: string, isInternal: boolean) {
    return this.http.post<FeedbackMessage>(
      `${FEEDBACK_BASE}/${id}/messages`,
      { body, is_internal: isInternal }
    );
  }

  loadRestartStatus(): void {
    this.http.get<RestartStatus>(`${ADMIN_BASE}/restart/status`)
      .subscribe({ next: s => this.pendingRestart.set(s.pending) });
  }

  triggerRestart() {
    return this.http.post<{ status: string }>(`${ADMIN_BASE}/restart`, {});
  }

  // Anomaly
  getAnomaly() { return this.http.get<AnomalySettings>(`${ADMIN_BASE}/anomaly`); }
  saveAnomaly(cfg: AnomalySettings) { return this.http.put<AnomalySettings>(`${ADMIN_BASE}/anomaly`, cfg); }

  // Billing
  getBilling() { return this.http.get<BillingSettings>(`${ADMIN_BASE}/billing`); }
  saveBilling(cfg: BillingSettings) { return this.http.put<BillingSettings>(`${ADMIN_BASE}/billing`, cfg); }

  // Stripe (payment gateway) - secret_key + webhook_secret returned masked as "***"
  getStripe() { return this.http.get<StripeSettings>(`${ADMIN_BASE}/stripe`); }
  saveStripe(cfg: StripeSettings) { return this.http.put<StripeSettings>(`${ADMIN_BASE}/stripe`, cfg); }

  // Billingo (invoicing) - api_key returned masked as "***"
  getBillingo() { return this.http.get<BillingoSettings>(`${ADMIN_BASE}/billingo`); }
  saveBillingo(cfg: BillingoSettings) { return this.http.put<BillingoSettings>(`${ADMIN_BASE}/billingo`, cfg); }

  // Rate Limits
  getRateLimits() { return this.http.get<RateLimitsSettings>(`${ADMIN_BASE}/ratelimits`); }
  saveRateLimits(cfg: RateLimitsSettings) { return this.http.put<RateLimitsSettings>(`${ADMIN_BASE}/ratelimits`, cfg); }

  // Ops
  getOps() { return this.http.get<OpsSettings>(`${ADMIN_BASE}/ops`); }
  saveOps(cfg: OpsSettings) { return this.http.put<OpsSettings>(`${ADMIN_BASE}/ops`, cfg); }

  // Deployment
  getDeployment() { return this.http.get<DeploymentSettings>(`${ADMIN_BASE}/deployment`); }
  saveDeployment(cfg: DeploymentSettings) { return this.http.put<DeploymentSettings>(`${ADMIN_BASE}/deployment`, cfg); }

  // Announcements
  getAnnouncements() { return this.http.get<AnnouncementsSettings>(`${ADMIN_BASE}/announcements`); }
  saveAnnouncements(cfg: AnnouncementsSettings) { return this.http.put<AnnouncementsSettings>(`${ADMIN_BASE}/announcements`, cfg); }

  // User limits (per-user tenant creation cap)
  getUserLimits() { return this.http.get<UserLimitsSettings>(`${ADMIN_BASE}/user-limits`); }
  saveUserLimits(cfg: UserLimitsSettings) { return this.http.put<UserLimitsSettings>(`${ADMIN_BASE}/user-limits`, cfg); }

  getBeta() { return this.http.get<BetaSettings>(`${ADMIN_BASE}/beta`); }
  saveBeta(cfg: BetaSettings) { return this.http.put<BetaSettings>(`${ADMIN_BASE}/beta`, cfg); }

  // Exchange rates (read-only, MNB source)
  getExchangeRates() {
    return this.http.get<ExchangeRatesResponse>(EXCHANGE_RATES_BASE);
  }
  getExchangeRateHistory(currency: string, days: number) {
    const params = { currency, days: String(days) };
    return this.http.get<ExchangeRateHistory>(`${EXCHANGE_RATES_BASE}/history`, { params });
  }

  // EU VAT rates (OSS destination rates; read-only + on-demand refresh)
  getVatRates() {
    return this.http.get<VatRatesResponse>(VAT_RATES_BASE);
  }
  refreshVatRates() {
    return this.http.post<VatRatesResponse>(`${VAT_RATES_BASE}/refresh`, {});
  }
}
